import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthContext } from 'modules/auth/context';
import { resolveSection, type SettingsSection } from './registry';

export interface SettingsDialogState {
  /** Dialog is open whenever `?settings=` is present. */
  isOpen: boolean;
  /** Active section, already permission-resolved (falls back to `profile`). */
  section: SettingsSection;
  /** Member drill-down inside the `members` section (`?member=<id>`). */
  memberId: string | null;
  /** Open on a section. Pushes history so the browser Back closes the dialog. */
  open: (section?: SettingsSection) => void;
  /** Switch section in place (replace — no history spam while browsing). */
  setSection: (section: SettingsSection) => void;
  /** Enter/leave the member detail view (replace). */
  setMember: (memberId: string | null) => void;
  /** Close and strip dialog params (replace — Back won't re-open it). */
  close: () => void;
}

/** `open` pushes (Back closes the dialog); everything else replaces, leaving no extra history entries. */
export function useSettingsDialog(): SettingsDialogState {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    state: { permissions },
  } = useAuthContext();

  const isOpen = searchParams.has('settings');
  const section = resolveSection(searchParams.get('settings'), permissions);
  const memberId = searchParams.get('member');

  const open = useCallback(
    (target: SettingsSection = 'profile') => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('settings', target);
        next.delete('member');
        return next;
      });
    },
    [setSearchParams],
  );

  const setSection = useCallback(
    (target: SettingsSection) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('settings', target);
          next.delete('member');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setMember = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('member', id);
          else next.delete('member');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('settings');
        next.delete('member');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return { isOpen, section, memberId, open, setSection, setMember, close };
}
